import { Compatibility } from '../helpers/compatibility';
import { debounce } from '../utils/debounce';
import { viewportChangeListener } from '../utils/viewport/viewport-change-listener';
import { ResizeMode } from './ResizeMode';
import { View } from './View';
import { isFullscreenMode, PIXEL_RATIO } from './globalFunctions';

export const RESIZE_HANDLER_TOKEN = Symbol('ResizeHandler');

export class ResizeHandler {
  public readonly debouncedResize: (event?: Event) => void;

  private readonly _view: View;
  protected _resizeMode: ResizeMode;

  private _top: number = 0;
  private _lastWH: number[];
  private _widthToHeight: number;
  private _pixelRatio: number;
  private readonly _minBackbufferWidth: number;

  constructor(view: View, resizeMode: ResizeMode, minBackbufferWidth: number = 0) {
    this._view = view;
    this._resizeMode = resizeMode;
    this._minBackbufferWidth = minBackbufferWidth;

    this.debouncedResize = debounce((event?: Event) => this.resize(event), 100);
  }

  public set top(top: number) {
    this._top = top;
  }

  public set resizeMode(resizeMode: ResizeMode) {
    this._resizeMode = resizeMode;
    this.resize();
  }

  public get resizeMode(): ResizeMode {
    return this._resizeMode;
  }

  public touchListener(): void {
    this._widthToHeight = this._view.canvas.width / this._view.canvas.height;
    this._pixelRatio = parseFloat(PIXEL_RATIO?.toString() ?? '1.0');
    this._lastWH = [
      Math.round(window.innerWidth * this._pixelRatio),
      Math.round(window.innerHeight * this._pixelRatio),
    ];

    viewportChangeListener.on((event) => this.debouncedResize(event));

    this.resize();
  }

  private resizeCanvas(event?: Event): void {
    let originalNewWidth: number = window.innerWidth;
    let originalNewHeight: number = window.innerHeight;

    const isFullScreenMode = isFullscreenMode();

    if (isFullScreenMode) {
      originalNewWidth = window.innerWidth;
      originalNewHeight = window.innerHeight;
    }

    const pixelRatio: number = parseFloat(PIXEL_RATIO?.toString() ?? '1.0');
    let newWidth: number = Math.round(originalNewWidth * pixelRatio);
    let newHeight: number = Math.round(originalNewHeight * pixelRatio);

    if (newWidth > 0 && newHeight > 0) {
      if (this._lastWH[0] !== newWidth || this._lastWH[1] !== newHeight || !event) {
        this._lastWH = [newWidth, newHeight];

        if (this._resizeMode === ResizeMode.ProportionalScale) {
          const newWidthToHeight = originalNewWidth / originalNewHeight;
          if (newWidthToHeight > this._widthToHeight) {
            newWidth = Math.round(newHeight * this._widthToHeight);
            originalNewWidth = Math.round(originalNewHeight * this._widthToHeight);
          } else {
            newHeight = Math.floor(newWidth / this._widthToHeight);
            originalNewHeight = Math.floor(originalNewWidth / this._widthToHeight);
          }
        }

        // Upscale the backbuffer (render resolution) on small/low-DPR windows while
        // keeping the CSS display size (originalNewWidth/Height) untouched, so the
        // layout/letterbox is unchanged but the scene renders sharper. No-op when 0.
        if (this._minBackbufferWidth > 0 && newWidth < this._minBackbufferWidth) {
          const k = this._minBackbufferWidth / newWidth;
          newWidth = Math.round(newWidth * k);
          newHeight = Math.round(newHeight * k);
        }

        switch (this._resizeMode) {
          case ResizeMode.Resize:
            this._view.canvas.width = newWidth;
            this._view.canvas.height = newHeight;
            this._view.canvas.style.width = `${originalNewWidth}px`;
            this._view.canvas.style.height = `${originalNewHeight}px`;
            break;

          case ResizeMode.Scale:
          case ResizeMode.ProportionalScale:
            this._view.canvas.width = newWidth;
            this._view.canvas.height = newHeight;
            this._view.canvas.style.width = `${originalNewWidth}px`;
            this._view.canvas.style.height = `${originalNewHeight}px`;
            this._view.canvas.style.top = `${this._top}px`;
            break;

          case ResizeMode.FillHeight: {
            const proportionalWidth = Math.round(originalNewHeight * this._widthToHeight);
            const cssWidth = Math.min(proportionalWidth, originalNewWidth);
            newWidth = Math.round(cssWidth * pixelRatio);
            this._view.canvas.width = newWidth;
            this._view.canvas.height = newHeight;
            this._view.canvas.style.width = `${cssWidth}px`;
            this._view.canvas.style.height = `${originalNewHeight}px`;
            this._view.canvas.style.top = `${this._top}px`;
            break;
          }
        }

        this._view.setWindowSize(newWidth, newHeight);
      }
    }
  }

  private resizeCanvasMobile(): void {
    const container = document.querySelector<HTMLElement>('#top-canvas-node')!;
    const topPanel = document.querySelector<HTMLElement>('#top-canvas-panel')!;
    if (topPanel) {
      topPanel.style.display = 'none';
    }

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const isPortrait = windowWidth < windowHeight;

    let originalRatio = 1657 / 768;

    const windowRatio = windowWidth / windowHeight;

    let canvasWidth: number;
    let canvasHeight: number;

    if (isPortrait) {
      originalRatio = 1 / originalRatio;
    }

    if (originalRatio > windowRatio) {
      canvasWidth = windowWidth;
      canvasHeight = Math.floor(canvasWidth / originalRatio);
    } else {
      canvasHeight = windowHeight;
      canvasWidth = Math.floor(canvasHeight * originalRatio);
    }

    if (windowWidth > 0 && windowHeight > 0) {
      container.style.margin = '0px';

      if (isPortrait) {
        this._view.canvas.width = Math.floor(windowWidth * this._pixelRatio);
        this._view.canvas.height = Math.floor(canvasHeight * this._pixelRatio);
        this._view.canvas.style.width = '100%';
        this._view.canvas.style.height = `${canvasHeight}px`;

        container.style.width = '100%';
        container.style.height = `${canvasHeight}px`;
      } else {
        this._view.canvas.width = Math.floor(canvasWidth * this._pixelRatio);
        this._view.canvas.height = Math.floor(windowHeight * this._pixelRatio);
        this._view.canvas.style.width = `${canvasWidth}px`;
        this._view.canvas.style.height = '100%';

        container.style.width = `${canvasWidth}px`;
        container.style.height = '100%';
      }

      this._view.setWindowSize(windowWidth, windowHeight);

      container.style.position = 'absolute';

      container.style.top = '0%';
      container.style.left = '50%';
      container.style.transform = 'translate(-50%, 0%)';
    }
  }

  private resize(event?: Event): void {
    if (Compatibility.IsMobileBrowser) {
      this.resizeCanvasMobile();
    } else {
      this.resizeCanvas(event);
    }
  }
}
